// pages/MobileCapturePage.jsx — Cible du QR desktop → mobile (route /m/:token).
//
// Le contexte (œuvre + action) est déjà connu : on ne redemande JAMAIS quelle œuvre
// ni quelle action. Capture caméra native (input capture) → prévisualisation → envoi
// → la fiche desktop se met à jour toute seule (realtime). Sécurité : RLS impose que
// le téléphone soit connecté au même compte (cf. lib/qrBridge).
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { resolveCaptureSession, markOpened, completeSession } from "@/lib/qrBridge";
import { getOrCreateUnclassifiedCollection } from "@/lib/unclassifiedCollection";

// Libellé + verbe selon l'action/le type de document (extensible aux autres cas).
function actionLabel(t, actionType, documentType) {
  const key = documentType || actionType;
  const map = {
    new_artwork: { verb: t("mobileCapture.newArtwork.heading"), noun: t("mobileCapture.newArtwork.noun") },
    invoice: { verb: t("mobileCapture.verb.scan"), noun: t("mobileCapture.doc.invoice") },
    certificate: { verb: t("mobileCapture.verb.scan"), noun: t("mobileCapture.doc.certificate") },
    verso: { verb: t("mobileCapture.verb.photo"), noun: t("mobileCapture.doc.verso") },
    signature: { verb: t("mobileCapture.verb.photo"), noun: t("mobileCapture.doc.signature") },
    condition: { verb: t("mobileCapture.verb.photo"), noun: t("mobileCapture.doc.condition") },
    detail: { verb: t("mobileCapture.verb.photo"), noun: t("mobileCapture.doc.detail") },
    main: { verb: t("mobileCapture.verb.photo"), noun: t("mobileCapture.doc.main") },
  };
  return map[key] || { verb: t("mobileCapture.verb.scan"), noun: t("mobileCapture.doc.generic") };
}

export default function MobileCapturePage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [phase, setPhase] = useState("loading"); // loading | needsAuth | ready | preview | sending | done | error
  const [reason, setReason] = useState("");
  const [session, setSession] = useState(null);
  const [artwork, setArtwork] = useState(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setPhase("needsAuth"); return; }

    let alive = true;
    (async () => {
      try {
        const res = await resolveCaptureSession(token);
        if (!alive) return;
        if (!res.ok) { setReason(res.reason); setPhase("error"); return; }
        setSession(res.session);
        await markOpened(res.session.id);
        if (res.session.artwork_id) {
          const { data } = await supabase.from("artworks").select("*").eq("id", res.session.artwork_id).maybeSingle();
          if (alive && data) setArtwork(data);
        }
        setPhase("ready");
      } catch {
        if (alive) { setReason("not_found"); setPhase("error"); }
      }
    })();
    return () => { alive = false; };
  }, [token, isAuthenticated, authLoading]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setPhase("preview");
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null); setPreviewUrl("");
    setPhase("ready");
    setTimeout(() => inputRef.current?.click(), 0);
  };

  const send = async () => {
    if (!file || !session) return;
    setPhase("sending");
    try {
      if (session.action_type === "new_artwork") {
        // Pré-fiche : on CRÉE une œuvre (client_id = compte connecté, collection
        // fournie ou « Œuvres non classées »), la photo devient l'image principale.
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("not_authenticated");
        const collectionId = session.collection_id || (await getOrCreateUnclassifiedCollection(user.id));

        const clean = file.name.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-zA-Z0-9.\-_]/g, "_");
        const fileName = `${uuidv4()}-${clean}`;
        const { error: upErr } = await supabase.storage.from("artworks-images").upload(fileName, file, {
          cacheControl: "3600", upsert: false, contentType: file.type || "image/jpeg",
        });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("artworks-images").getPublicUrl(fileName);
        const url = pub?.publicUrl;
        const img = {
          id: `img_${Date.now()}_${uuidv4().slice(0, 8)}`, url, path: url, publicUrl: url,
          name: file.name, size: file.size, type: file.type, isPrimary: true,
          kind: "main", uploadedAt: new Date().toISOString(),
        };
        const { data: created, error: insErr } = await supabase.from("artworks")
          .insert({
            client_id: user.id,
            collection_id: collectionId,
            title: t("mobileCapture.newArtwork.defaultTitle"),
            images: [img],
            image_url: url,
            is_draft: true, // pré-fiche incomplète → badge « Brouillon » jusqu'à complétion
          })
          .select("id")
          .single();
        if (insErr) throw insErr;
        // On renvoie l'id de la nouvelle œuvre → le desktop ouvre la pré-fiche.
        await completeSession(session.id, created.id);
      } else if (session.action_type === "upload_image") {
        // Verso / signature / détail / vue principale → bucket images + tableau
        // canonique artworks.images (avec isPrimary), comme l'upload desktop.
        const clean = file.name.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-zA-Z0-9.\-_]/g, "_");
        const fileName = `${uuidv4()}-${clean}`;
        const { error: upErr } = await supabase.storage.from("artworks-images").upload(fileName, file, {
          cacheControl: "3600", upsert: false, contentType: file.type || "image/jpeg",
        });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("artworks-images").getPublicUrl(fileName);
        const url = pub?.publicUrl;
        const current = Array.isArray(artwork?.images) ? artwork.images : [];
        // Même forme d'objet + même règle image_url que handleAddImages (ArtworkEdit).
        const newImg = {
          id: `img_${Date.now()}_${uuidv4().slice(0, 8)}`, url, path: url, publicUrl: url,
          name: file.name, size: file.size, type: file.type,
          isPrimary: current.length === 0, kind: session.document_type || "photo",
          uploadedAt: new Date().toISOString(),
        };
        const allImages = [...current, newImg];
        const primaryUrl = allImages.find((i) => i.isPrimary)?.url || allImages[0]?.url || null;
        const { error: updErr } = await supabase.from("artworks")
          .update({ images: allImages, image_url: primaryUrl })
          .eq("id", session.artwork_id);
        if (updErr) throw updErr;
        await completeSession(session.id, url);
      } else if (session.action_type === "placement_context") {
        // Photo de CONTEXTE PRIVÉE d'un placement → bucket privé placement-photos.
        // On renvoie le CHEMIN (pas d'URL publique) ; le desktop l'attache au placement.
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("not_authenticated");
        const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
        const path = `${user.id}/${session.artwork_id}/ctx_${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("placement-photos").upload(path, file, {
          contentType: file.type || "image/jpeg", upsert: true,
        });
        if (upErr) throw upErr;
        await completeSession(session.id, path);
      } else {
        // Facture / certificat / constat → bucket documents + entrée dans la colonne
        // artworks.documents (doc de première classe, lisible par l'OCR d'enrichissement).
        const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
        const slug = { invoice: "facture", certificate: "certificat", condition: "constat" }[session.document_type] || "document";
        const filename = `${uuidv4()}-${slug}.${ext}`;
        const path = `${session.artwork_id}/${filename}`;
        const { error: upErr } = await supabase.storage.from("documents").upload(path, file, {
          contentType: file.type || "image/jpeg", upsert: false,
        });
        if (upErr) throw upErr;

        // Métadonnées → colonne (dédup par nom côté onglet Documents ; l'OCR lit cette colonne).
        const colType = { invoice: "invoice", certificate: "certificate", condition: "condition_report" }[session.document_type] || "other";
        let currentDocs = [];
        try {
          const d = artwork?.documents;
          currentDocs = Array.isArray(d) ? d : (typeof d === "string" ? JSON.parse(d) : []);
        } catch { currentDocs = []; }
        const entry = {
          id: uuidv4(), name: `${slug}.${ext}`, type: colType,
          storagePath: path, bucket: "documents", uploadedAt: new Date().toISOString(),
        };
        await supabase.from("artworks").update({ documents: [...currentDocs, entry] }).eq("id", session.artwork_id);
        await completeSession(session.id, path);
      }
      setPhase("done");
    } catch (e) {
      setReason(e?.message || "upload_failed");
      setPhase("error");
    }
  };

  const label = session ? actionLabel(t, session.action_type, session.document_type) : null;
  const artworkTitle = artwork?.title || artwork?.name || "";
  const artistName = artwork?.artist || artwork?.artist_name || artwork?.artist_full_name || "";

  return (
    <div className="min-h-screen bg-[#f9f7f4] flex flex-col" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <div className="px-5 py-4 border-b border-[#e5e0d8]">
        <div className="text-xs tracking-[0.25em] uppercase text-[#c9a961]">ARTKIV</div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 text-center">
        {phase === "loading" && (
          <div className="w-12 h-12 border-2 border-[#e5e0d8] border-t-[#c9a961] rounded-full animate-spin" />
        )}

        {phase === "needsAuth" && (
          <div className="max-w-sm">
            <h1 className="text-2xl font-bold text-[#1a1a1a] mb-3">{t("mobileCapture.auth.title")}</h1>
            <p className="text-[#6b6459] mb-6">{t("mobileCapture.auth.body")}</p>
            <Link to={`/login?redirect=${encodeURIComponent(`/m/${token}`)}`}
              className="inline-block px-6 py-3 rounded-lg bg-[#c9a961] text-[#1a1a1a] font-semibold hover:bg-[#d8bd7e] transition-colors">
              {t("mobileCapture.auth.cta")}
            </Link>
          </div>
        )}

        {phase === "error" && (
          <div className="max-w-sm">
            <div className="text-3xl mb-3" aria-hidden="true">✕</div>
            <h1 className="text-xl font-bold text-[#1a1a1a] mb-2">{t("mobileCapture.error.title")}</h1>
            <p className="text-[#6b6459]">
              {reason === "expired" ? t("mobileCapture.error.expired")
                : reason === "completed" ? t("mobileCapture.error.completed")
                : t("mobileCapture.error.generic")}
            </p>
          </div>
        )}

        {(phase === "ready" || phase === "preview" || phase === "sending") && label && (
          <div className="w-full max-w-sm">
            <div className="text-xs uppercase tracking-wide text-[#8a8378] mb-2">{label.noun}</div>
            <h1 className="text-2xl font-bold text-[#1a1a1a] leading-tight">
              {label.verb} {artworkTitle && <span>— {artworkTitle}</span>}
            </h1>
            {artistName && <p className="text-[#6b6459] mt-1">{artistName}</p>}
            <div className="mt-3 h-px w-12 bg-[#c9a961] mx-auto" />

            {phase !== "preview" && phase !== "sending" && (
              <>
                <button
                  onClick={() => inputRef.current?.click()}
                  className="mt-8 w-full py-4 rounded-xl bg-[#1a1a1a] text-[#f9f7f4] font-semibold text-lg hover:bg-black transition-colors"
                >
                  {t("mobileCapture.takePhoto")}
                </button>
                <p className="mt-3 text-xs text-[#8a8378]">{t("mobileCapture.hint")}</p>
              </>
            )}

            {(phase === "preview" || phase === "sending") && previewUrl && (
              <>
                <img src={previewUrl} alt="" className="mt-6 w-full rounded-xl border border-[#e5e0d8] object-contain max-h-[50vh]" />
                <button
                  onClick={send}
                  disabled={phase === "sending"}
                  className="mt-5 w-full py-4 rounded-xl bg-[#c9a961] text-[#1a1a1a] font-semibold text-lg hover:bg-[#d8bd7e] transition-colors disabled:opacity-60"
                >
                  {phase === "sending" ? t("mobileCapture.sending") : t("mobileCapture.use")}
                </button>
                {phase !== "sending" && (
                  <button onClick={retake} className="mt-3 w-full py-3 text-[#6b6459] underline underline-offset-4">
                    {t("mobileCapture.retake")}
                  </button>
                )}
              </>
            )}

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPick}
              className="hidden"
            />
          </div>
        )}

        {phase === "done" && (
          <div className="max-w-sm">
            <div className="w-16 h-16 rounded-full bg-[#e9f3ec] text-[#3b7d4f] text-3xl flex items-center justify-center mx-auto mb-4" aria-hidden="true">✓</div>
            <h1 className="text-2xl font-bold text-[#1a1a1a] mb-2">{t("mobileCapture.done.title")}</h1>
            <p className="text-[#6b6459]">{t("mobileCapture.done.body")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
